import { useEffect, useState } from "react";
import { useLocation, Link } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { MessageSquare } from "lucide-react";
import { ConversationChat } from "@/components/conversation-chat";

export type InquirySummary = {
  id: number;
  listingId: number;
  sellerId: number;
  buyerId: number;
  lastMessagePreview: string | null;
  lastMessageAt: string;
  unreadCount: number;
  listingPropertyName: string;
  listingPropertyAddress: string | null;
  listingAskingPrice: string;
  listingPhotos: string[];
  listingStatus: string;
  sellerDisplayName: string | null;
  buyerDisplayName: string | null;
};

export function BuyerInquiriesView({
  currentUserId,
  initialInquiryId,
  viewListingHref,
  emptyDescription,
}: {
  currentUserId: number;
  initialInquiryId: number | null;
  viewListingHref?: (listingId: number) => string;
  emptyDescription?: string;
}) {
  const [, navigate] = useLocation();
  const { data: inquiries, isLoading } = useQuery<InquirySummary[]>({
    queryKey: ["/api/app/sell/inquiries"],
    refetchInterval: 15000,
  });

  const myInquiries = (inquiries || []).filter((i) => i.buyerId === currentUserId);
  const [activeId, setActiveId] = useState<number | null>(initialInquiryId);
  const active = myInquiries.find((i) => i.id === activeId) || myInquiries[0];

  useEffect(() => {
    if (activeId === null && myInquiries.length > 0) setActiveId(myInquiries[0].id);
  }, [myInquiries.length, activeId, myInquiries]);

  if (isLoading) {
    return <div className="text-sm text-gray-500">Loading conversations...</div>;
  }

  if (myInquiries.length === 0) {
    return (
      <div className="text-center text-gray-500 py-12 border border-dashed border-gray-200 rounded-xl bg-white" data-testid="buyer-inquiries-empty">
        <div className="w-16 h-16 mx-auto rounded-full bg-gray-100 flex items-center justify-center mb-3">
          <MessageSquare className="h-8 w-8 text-gray-400" />
        </div>
        <p className="text-lg font-medium text-gray-700">You haven't started any inquiries yet</p>
        <p className="text-sm mt-1">{emptyDescription || "Browse the Marketplace and click 'Contact Seller' on a listing to start a conversation."}</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="buyer-inquiries-view">
      <div className="md:col-span-1 border border-gray-200 rounded-xl bg-white overflow-hidden flex flex-col h-[480px]">
        <div className="px-4 py-3 border-b border-gray-100">
          <h3 className="font-medium text-gray-900">My Conversations</h3>
          <p className="text-xs text-gray-500">{myInquiries.length} active {myInquiries.length === 1 ? "chat" : "chats"}</p>
        </div>
        <div className="flex-1 overflow-y-auto">
          {myInquiries.map((i) => {
            const isActive = active?.id === i.id;
            return (
              <button
                key={i.id}
                onClick={() => setActiveId(i.id)}
                className={`w-full text-left px-4 py-3 border-b border-gray-100 hover:bg-gray-50 ${isActive ? "bg-blue-50 border-l-4 border-l-blue-600" : ""}`}
                data-testid={`buyer-inquiry-row-${i.id}`}
              >
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-900 truncate" data-testid={`buyer-inquiry-property-${i.id}`} title={i.listingPropertyName || undefined}>
                    {i.listingPropertyName}
                  </span>
                  {i.unreadCount > 0 && (
                    <Badge className="bg-blue-600 text-white border-0 text-[10px]">{i.unreadCount}</Badge>
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-0.5 truncate">
                  {i.sellerDisplayName ? `Seller: ${i.sellerDisplayName}` : ""}
                </p>
                <p className="text-xs text-gray-500 mt-1 truncate">{i.lastMessagePreview || "No messages yet"}</p>
              </button>
            );
          })}
        </div>
      </div>
      <div className="md:col-span-2 space-y-3">
        {active ? (
          <>
            <div className="border border-gray-200 rounded-lg bg-white px-4 py-3 flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-900">{active.listingPropertyName}</p>
                {active.listingPropertyAddress && <p className="text-xs text-gray-500">{active.listingPropertyAddress}</p>}
              </div>
              <Button
                size="sm"
                variant="outline"
                asChild
                data-testid={`button-view-listing-${active.listingId}`}
              >
                <Link href={viewListingHref ? viewListingHref(active.listingId) : `/app/sell/${active.listingId}`}>View Listing</Link>
              </Button>
            </div>
            <ConversationChat
              source="inquiry"
              conversationId={active.id}
              currentUserId={currentUserId}
              counterpartyName={active.sellerDisplayName || "the seller"}
              disabled={active.listingStatus !== "active"}
              disabledPlaceholder="This listing is no longer accepting messages."
            />
          </>
        ) : (
          <div className="border border-gray-200 rounded-xl bg-white p-8 text-center text-gray-500 h-[480px] flex flex-col items-center justify-center">
            <MessageSquare className="h-10 w-10 text-gray-300 mb-2" />
            <p className="text-sm">Select a conversation to view messages.</p>
          </div>
        )}
      </div>
    </div>
  );
}
